import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import { useAppDispatch } from "src/store/hooks";
import {
  AirlineData,
  AirlineDataForm,
  AirlineEditDataForm,
  addAirline,
  refreshAirlineList,
  updateAirline,
} from "src/store/reducers/airlines";
import {
  EmailRequired,
  KeyRequired,
  StringLengthValidation,
} from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";

type IAirlineFormCanvas = {
  Title: string;
  show: boolean;
  editId: string;
  hide: () => void;
  airlineData?: AirlineData;
};

const AirlineFormCanvas = ({
  Title,
  show,
  hide,
  editId,
  airlineData,
}: IAirlineFormCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();

  const {
    handleChange,
    setErrors,
    values,
    handleSubmit,
    touched,
    errors,
    resetForm,
  } = useFormik({
    initialValues: {
      airline_name: airlineData?.airline_name || "",
      airline_code: airlineData?.airline_code || "",
      contact_number: airlineData?.contact_number || "",
      contact_person_name: airlineData?.contact_person_name || "",
      email_id: airlineData?.email_id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      airline_name: StringLengthValidation("Airline name", 2, 50),
      airline_code: StringLengthValidation("Airline code", 2, 10),
      contact_person_name: StringLengthValidation("Contact person", 3, 30),
      contact_number: KeyRequired("Contact number"),
      email_id: EmailRequired("Email address", false),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (editId !== "Add") {
        setPending(true);
        const formValue = { ...values, id: editId };
        editU(formValue);
      } else {
        setPending(true);
        addU(values);
      }
    },
  });

  const handleHide = useCallback(() => {
    hide();
  }, [hide]);

  const addU = useCallback(
    async (values: Omit<AirlineDataForm, "id">) => {
      try {
        await dispatch(addAirline(values)).unwrap();
        handleHide();
        toast.success("Airline is added successfully");
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  const editU = useCallback(
    async (values: AirlineEditDataForm) => {
      try {
        await dispatch(updateAirline(values)).unwrap();
        toast.success("Airline is updated successfully");
        dispatch(refreshAirlineList());
        handleHide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  useEffect(() => {
    resetForm();
  }, [resetForm, show]);

  return (
    <Offcanvas
      show={show}
      onHide={handleHide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h5 className="modal-title" id="#gridSystemModal">
          {Title}
        </h5>
        <button type="button" className="btn-close" onClick={handleHide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="airline_name"
                  labelText="Airline Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.airline_name}
                  touched={touched.airline_name}
                  errors={errors.airline_name}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="airline_code"
                  labelText="Airline Code"
                  required={true}
                  handleChange={handleChange}
                  value={values.airline_code}
                  touched={touched.airline_code}
                  errors={errors.airline_code}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_person_name"
                  labelText="Contact Person Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.contact_person_name}
                  touched={touched.contact_person_name}
                  errors={errors.contact_person_name}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_number"
                  labelText="Contact number"
                  required={true}
                  handleChange={handleChange}
                  value={values.contact_number}
                  touched={touched.contact_number}
                  errors={errors.contact_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="email_id"
                  inputType="email"
                  labelText="Email"
                  handleChange={handleChange}
                  value={values.email_id}
                  touched={touched.email_id}
                  errors={errors.email_id}
                />{" "}
              </div>
              <div>
                <button
                  type="submit"
                  disabled={pending}
                  className="btn btn-primary me-1"
                >
                  Submit
                </button>
                <Link
                  to={"#"}
                  onClick={handleHide}
                  className="btn btn-danger light ms-1"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default AirlineFormCanvas;
